import * as THREE from 'three/webgpu';
import {PointsMaterialFactory} from '../../material/PointsMaterialFactory';
import {
	isSketchPointObject,
	SketchPointKind,
	type SketchGroup,
} from '../../mesh/sketch';
import type {Viewer} from '../../viewer/Viewer';
import {AbstractSketchEntitiesTransformController} from './AbstractSketchEntitiesTransformController';
import {
	SketchEntitiesTransformCommand,
	SketchEntitiesTransformInputMode,
	SketchEntitiesTransformPhase,
	type SketchEntitiesTransformRepeatRecord,
	SketchEntitiesTransformSelectionRole,
} from './SketchEntitiesTransformTypes';
import {
	clearSketchEntitiesTransformPointSnapFeedback,
	createSketchEntitiesTransformPointSnapExclusionIds,
	resolveSketchEntitiesTransformPointSnap,
} from './utils/SketchEntitiesTransformPointSnapResolver';
import {SketchPlanePointerIntersectionResolver} from './SketchPlanePointerIntersectionResolver';
import {SketchReferencedEntityCopyConstraintPolicy} from './SketchReferencedEntityCopyConstraintPolicy';
import {SketchCopyEntitiesPreviewSession} from './SketchCopyEntitiesPreviewSession';

/**
 * 复制实体 X/Y 重复轴。
 */
enum SketchCopyEntitiesXYRepeatAxis {
	/** 未记录重复轴。 */
	None = 'none',
	/** 重复 X 轴输入值。 */
	X = 'x',
	/** 重复 Y 轴输入值。 */
	Y = 'y',
}

/**
 * 草图复制实体控制器。
 */
export class SketchCopyEntitiesController extends AbstractSketchEntitiesTransformController {
	/** 起点黄色标记根对象。 */
	private fromPointMarkerRoot: THREE.Group | null = null;
	/** 鼠标驱动预览参考平面。 */
	private pointerDrivenPreviewPlane: THREE.Plane | null = null;
	/** 鼠标驱动预览当前目标点。 */
	private pointerDrivenPreviewPoint: THREE.Vector3 | null = null;
	/** 当前复制预览副本域会话。 */
	private copyPreviewSession: SketchCopyEntitiesPreviewSession | null = null;
	/** 当前选择域与预览副本域共用的点捕捉排除 ID 缓存。 */
	private pointSnapExclusionIds: ReadonlySet<string> | null = null;
	/** X/Y 模式下 X 方向重复步长。 */
	private xyRepeatStepX = 0;
	/** X/Y 模式下 Y 方向重复步长。 */
	private xyRepeatStepY = 0;
	/** X/Y 模式下当前允许重复的单一轴。 */
	private xyRepeatAxis = SketchCopyEntitiesXYRepeatAxis.None;
	/** 鼠标到草图平面的自由求交解析器。 */
	private readonly planePointerIntersectionResolver = new SketchPlanePointerIntersectionResolver();
	/** 转换实体引用对象的通用复制约束策略。 */
	private readonly referencedEntityConstraintPolicy: SketchReferencedEntityCopyConstraintPolicy;

	/**
	 * @param viewer Viewer 实例。
	 * @param referencedEntityConstraintPolicy 可注入的转换实体复制约束策略。
	 */
	public constructor(
		viewer: Viewer,
		referencedEntityConstraintPolicy = new SketchReferencedEntityCopyConstraintPolicy(),
	) {
		super(viewer);
		this.referencedEntityConstraintPolicy = referencedEntityConstraintPolicy;
	}

	/**
	 * 解析复制实体命令的单个选择对象。
	 *
	 * 设计说明：
	 * 1. 控制点只用于定义所属草图几何，不能作为复制实体命令的独立选择对象；
	 * 2. 命中控制点时必须立即拒绝，禁止继续使用父类策略提升到矩形、直线或曲线；
	 * 3. 普通草图点和其他真实草图实体继续复用父类统一解析能力。
	 *
	 * @param object 原始命中对象。
	 * @param activeSketchGroup 当前活动草图组。
	 * @returns 可复制草图实体；控制点返回 null。
	 */
	protected override resolveTransformEntity(
		object: THREE.Object3D,
		activeSketchGroup: SketchGroup,
	): THREE.Object3D | null {
		if (isSketchPointObject(object) && object.pointKind === SketchPointKind.ControlPoint) {
			return null;
		}
		return super.resolveTransformEntity(object, activeSketchGroup);
	}

	/**
	 * 解析复制实体命令的批量选择对象。
	 *
	 * 设计说明：
	 * 1. 启动前预选和命令内框选必须与单击保持同一控制点排除规则；
	 * 2. 先排除所有控制点，再交给父类完成轮廓、集合归一和 UUID 去重；
	 * 3. 该重写只影响复制实体，移动实体和旋转实体继续使用父类默认策略。
	 *
	 * @param objects 原始选择对象集合。
	 * @param activeSketchGroup 当前活动草图组。
	 * @returns 去重后的可复制草图实体集合。
	 */
	protected override resolveTransformEntities(
		objects: readonly THREE.Object3D[],
		activeSketchGroup: SketchGroup,
	): THREE.Object3D[] {
		const copyableObjects = objects.filter((object) => (
			!isSketchPointObject(object) || object.pointKind !== SketchPointKind.ControlPoint
		));
		return super.resolveTransformEntities(copyableObjects, activeSketchGroup);
	}

	
	public override start(initialEntities: readonly THREE.Object3D[] = []): void {
		this.pointSnapExclusionIds = null;
		super.start(initialEntities);
		this.resetXYRepeatStep();
		this.syncFromPointMarker();
	}

	
	public override clear(): void {
		this.pointSnapExclusionIds = null;
		this.resetPointerDrivenPreview();
		this.clearFromPointMarker();
		clearSketchEntitiesTransformPointSnapFeedback(this.viewer);
		this.resetXYRepeatStep();
		super.clear();
	}

	
	public override dispose(): void {
		this.pointSnapExclusionIds = null;
		this.resetPointerDrivenPreview();
		this.clearFromPointMarker();
		clearSketchEntitiesTransformPointSnapFeedback(this.viewer);
		super.dispose();
	}

	
	public override setInputMode(mode: SketchEntitiesTransformInputMode): void {
		if (mode !== SketchEntitiesTransformInputMode.FromTo) {
			this.resetPointerDrivenPreview();
			clearSketchEntitiesTransformPointSnapFeedback(this.viewer);
		}
		// X/Y 模式使用明确的位移默认值，保证面板显示、确认态与预览计算一致。
		if (mode === SketchEntitiesTransformInputMode.Coordinate) {
			this.targetX ??= 0;
			this.targetY ??= 0;
			this.xyRepeatStepX = this.targetX ?? 0;
			this.xyRepeatStepY = this.targetY ?? 0;
			this.xyRepeatAxis = SketchCopyEntitiesXYRepeatAxis.None;
		}
		super.setInputMode(mode);
		this.syncFromPointMarker();
	}

	//------ 复制实体XY重复数值 开始------
	
	public override setTargetCoordinate(axis: 'x' | 'y', value: number | null): void {
		if (axis === 'x') {
			this.xyRepeatStepX = value ?? 0;
			this.xyRepeatAxis = SketchCopyEntitiesXYRepeatAxis.X;
		} else {
			this.xyRepeatStepY = value ?? 0;
			this.xyRepeatAxis = SketchCopyEntitiesXYRepeatAxis.Y;
		}
		super.setTargetCoordinate(axis, value);
	}

	/**
	 * 重置 X/Y 重复步长。
	 */
	private resetXYRepeatStep(): void {
		this.xyRepeatStepX = 0;
		this.xyRepeatStepY = 0;
		this.xyRepeatAxis = SketchCopyEntitiesXYRepeatAxis.None;
	}
	//------ 复制实体XY重复数值 结束------

	
	public override handleReferencePointSelection(candidate: Parameters<AbstractSketchEntitiesTransformController['handleReferencePointSelection']>[0]): boolean {
		const shouldStartPointerDrivenPreview = this.inputMode === SketchEntitiesTransformInputMode.FromTo
			&& this.activeSelectionRole === SketchEntitiesTransformSelectionRole.FromPoint;
		if (shouldStartPointerDrivenPreview) {
			this.resetPointerDrivenPreview();
		}
		const handled = super.handleReferencePointSelection(candidate);
		if (!handled) {
			return false;
		}
		if (shouldStartPointerDrivenPreview && this.fromPoint) {
			this.initializePointerDrivenPreview(this.fromPoint.position);
			this.activeSelectionRole = SketchEntitiesTransformSelectionRole.None;
			this.refreshPhase();
			this.notifyStateChanged();
		}
		this.syncFromPointMarker();
		return true;
	}

	/**
	 * 判断当前是否处于鼠标驱动预览态。
	 *
	 * @returns 是否处于鼠标驱动预览态。
	 */
	public isPointerDrivenPreviewActive(): boolean {
		return this.inputMode === SketchEntitiesTransformInputMode.FromTo
			&& !!this.fromPoint
			&& !!this.pointerDrivenPreviewPlane;
	}

	/**
	 * 根据视口鼠标位置刷新复制预览。
	 *
	 * 说明：
	 * 1. 复制实体与移动实体共享 SolidWorks 的“起点抓手”交互；
	 * 2. 起点选定后，鼠标在草图基准面上的交点就是副本落点；
	 * 3. 每帧都基于原始起点重新计算绝对位移，避免累计误差。
	 *
	 * @param clientX 屏幕 X 坐标。
	 * @param clientY 屏幕 Y 坐标。
	 * @returns 是否已消费本次鼠标移动。
	 */
	public handlePointerDrivenPreviewMove(clientX: number, clientY: number): boolean {
		if (!this.isPointerDrivenPreviewActive()) {
			return false;
		}

		const nextPoint = this.resolvePointerDrivenPreviewPoint(clientX, clientY);
		if (!nextPoint) {
			return false;
		}

		this.pointerDrivenPreviewPoint = nextPoint;
		const previousPhase = this.phase;
		this.tryPreview();
		// 鼠标目标点不属于 PropertyManager 快照；只有阶段真正变化时才通知 UI。
		if (this.phase !== previousPhase) {
			this.notifyStateChanged();
		}
		return true;
	}

	/**
	 * 处理鼠标驱动预览态下的落点点击。
	 *
	 * @returns 是否已消费本次点击。
	 */
	public handlePointerDrivenPreviewPlacement(): boolean {
		if (!this.isPointerDrivenPreviewActive()) {
			return false;
		}
		return this.confirm();
	}

	//------ 复制实体XY重复数值 开始------
	
	public override repeat(): boolean {
		if (this.inputMode !== SketchEntitiesTransformInputMode.Coordinate) {
			return super.repeat();
		}
		if (!this.canRepeat()) {
			return false;
		}

		// Repeat 在 X/Y 模式下一次只重复最后编辑的轴，保持与移动实体一致的连续步进语义。
		if (this.xyRepeatAxis === SketchCopyEntitiesXYRepeatAxis.X) {
			this.targetX = (this.targetX ?? 0) + this.xyRepeatStepX;
		}
		if (this.xyRepeatAxis === SketchCopyEntitiesXYRepeatAxis.Y) {
			this.targetY = (this.targetY ?? 0) + this.xyRepeatStepY;
		}
		this.refreshPhase();
		this.tryPreview();
		this.notifyStateChanged();
		return true;
	}
	//------ 复制实体XY重复数值 结束------

	
	protected resolveCommandType(): SketchEntitiesTransformCommand {
		return SketchEntitiesTransformCommand.Copy;
	}

	
	protected resolveTransformMatrix(): THREE.Matrix4 {
		return this.geometryService.createTranslationMatrix(this.resolveTranslationVector());
	}

	
	protected applyResolvedTransform(matrix: THREE.Matrix4, sketchGroup: SketchGroup): void {
		const clones = this.cloneEntitiesForCopy(this.selectedEntities, sketchGroup);
		// 正式副本必须与预览使用同一套直接落位规则，避免确认后被副本关系再次求解而发生跳动。
		this.geometryService.applyMatrixToEntitiesWithoutConstraintSolve(clones, matrix);
		this.viewer.syncSketchEditingVisualLayering();
	}

	//------ 复制实体手动预览 开始------
	
	protected override refreshPhase(): void {
		if (!this.active) {
			this.phase = SketchEntitiesTransformPhase.Cancelled;
			return;
		}
		if (this.canConfirm()) {
			this.phase = this.copyPreviewSession
				? SketchEntitiesTransformPhase.Previewing
				: SketchEntitiesTransformPhase.Ready;
			return;
		}
		this.phase = this.activeSelectionRole === SketchEntitiesTransformSelectionRole.Entities
			? SketchEntitiesTransformPhase.SelectEntities
			: SketchEntitiesTransformPhase.SelectReference;
	}

	
	protected override tryPreview(): void {
		if (!this.active) {
			return;
		}
		if (!this.canConfirm()) {
			this.rollbackPreview();
			return;
		}

		const activeSketchGroup = this.resolveActiveSketchGroup();
		if (!activeSketchGroup) {
			return;
		}

		const translation = this.resolveTranslationVector();
		if (translation.lengthSq() <= 1e-20) {
			this.rollbackPreview();
			return;
		}

		const createdPreviewSession = !this.copyPreviewSession;
		if (!this.copyPreviewSession) {
			const previewClones = this.cloneEntitiesForCopy(this.selectedEntities, activeSketchGroup);
			this.copyPreviewSession = new SketchCopyEntitiesPreviewSession(
				previewClones,
				this.geometryService,
			);
			// 新增副本域后捕捉排除集合发生变化，下一帧按稳定对象集合重建一次即可。
			this.pointSnapExclusionIds = null;
		}

		try {
			// 预览会话内部只补充相邻绝对目标之间的位移，不再重建副本、关系或持久身份。
			this.copyPreviewSession.applyTranslation(translation);
		} catch (error) {
			// 任一实体更新失败都终止整套预览域，禁止把半更新副本留在活动草图中。
			this.rollbackPreview();
			throw error;
		}

		if (createdPreviewSession) {
			// 预览根只在副本域创建时登记一次；同步入口本身已经包含一次即时渲染。
			this.syncPreviewVisualLayering();
		} else {
			// 后续 mousemove 只更新已有几何，并执行一次同步渲染。
			this.viewer.requestRender();
		}
		this.refreshPhase();
	}

	
	protected override rollbackPreview(): void {
		// 父控制器会在选择和参考点变化前统一调用回滚，因此这里也是捕捉缓存的失效边界。
		this.pointSnapExclusionIds = null;
		if (!this.copyPreviewSession) {
			this.refreshPhase();
			return;
		}

		this.copyPreviewSession.dispose();
		this.copyPreviewSession = null;
		// 退出副本域时重建一次预览对象索引；同步入口负责完成本次渲染。
		this.syncPreviewVisualLayering();
		this.refreshPhase();
	}
	//------ 复制实体手动预览 结束------

	
	public override confirm(): boolean {
		const confirmed = super.confirm();
		if (confirmed) {
			this.viewer.clearSketchCopyEntitiesMode();
		}
		return confirmed;
	}

	
	public override cancel(): void {
		this.resetPointerDrivenPreview();
		this.rollbackPreview();
		this.phase = SketchEntitiesTransformPhase.Cancelled;
		this.syncFromPointMarker();
		clearSketchEntitiesTransformPointSnapFeedback(this.viewer);
		this.notifyStateChanged();
	}

	
	protected resolveHistoryCommandType(): string {
		return 'sketch.runtime.copy-entities';
	}

	
	protected resolveHistoryLabel(): string {
		return '复制草图实体';
	}

	
	protected canConfirm(): boolean {
		if (this.selectedEntities.length === 0) {
			return false;
		}
		if (this.inputMode === SketchEntitiesTransformInputMode.FromTo) {
			return !!this.fromPoint;
		}
		return this.targetX !== null || this.targetY !== null;
	}

	
	protected canRepeat(): boolean {
		if (this.inputMode === SketchEntitiesTransformInputMode.Coordinate) {
			return this.selectedEntities.length > 0
				&& this.resolveActiveSketchGroup() !== null
				&& this.xyRepeatAxis !== SketchCopyEntitiesXYRepeatAxis.None
				&& this.resolveXYRepeatStepTranslationVector().lengthSq() > 1e-20;
		}
		return this.lastRepeatRecord !== null;
	}

	
	protected applyRepeatRecord(record: SketchEntitiesTransformRepeatRecord, sketchGroup: SketchGroup): boolean {
		const entities = this.geometryService.resolveEntitiesByIds(sketchGroup, record.sourceEntityIds);
		if (entities.length === 0) {
			return false;
		}
		this.viewer.executeActiveSketchRuntimeHistoryTransaction({
			label: this.resolveHistoryLabel(),
			commandType: this.resolveHistoryCommandType(),
			work: () => {
				const clones = this.cloneEntitiesForCopy(entities, sketchGroup);
				// Repeat 与首次确认必须保持一致：普通关系随副本保留，转换实体关系在克隆阶段过滤。
				this.geometryService.applyMatrixToEntitiesWithoutConstraintSolve(clones, record.matrix);
				sketchGroup.markDefinitionDirty({
					reason: this.resolveHistoryCommandType(),
					sourceObjectId: clones[0]?.uuid,
				});
				sketchGroup.commitDefinitionChanges({
					reason: this.resolveHistoryCommandType(),
					sourceObjectId: clones[0]?.uuid,
				});
				this.viewer.syncSketchEditingVisualLayering();
				return true;
			},
		});
		return true;
	}

	/**
	 * 按复制实体命令规则创建副本。
	 *
	 * 设计说明：
	 * 1. 预览、确认与 Repeat 必须共用同一入口，避免不同阶段产生不同约束状态；
	 * 2. 转换实体引用生成的对象不复制实体本体及关联点约束，普通草图对象仍沿用默认约束克隆；
	 * 3. 过滤发生在副本创建阶段，不修改原实体，也不允许约束求解器短暂接管副本位置。
	 *
	 * @param sourceEntities 本次复制的源实体。
	 * @param sketchGroup 目标草图组。
	 * @returns 已挂载到目标草图组的副本域。
	 */
	private cloneEntitiesForCopy(
		sourceEntities: readonly THREE.Object3D[],
		sketchGroup: SketchGroup,
	): THREE.Object3D[] {
		const shouldCloneConstraint = this.referencedEntityConstraintPolicy
			.createClonePredicate(sourceEntities);
		return this.geometryService.cloneEntities(
			sourceEntities,
			sketchGroup,
			undefined,
			undefined,
			shouldCloneConstraint,
		);
	}

	/**
	 * 解析复制位移向量。
	 *
	 * @returns 位移向量。
	 */
	private resolveTranslationVector(): THREE.Vector3 {
		if (this.inputMode === SketchEntitiesTransformInputMode.FromTo && this.fromPoint) {
			const targetPoint = this.pointerDrivenPreviewPoint?.clone()
				?? this.toPoint?.position.clone()
				?? this.fromPoint.position.clone();
			return targetPoint.sub(this.fromPoint.position);
		}
		return this.resolveXYStepTranslationVector();
	}

	//------ 复制实体XY重复数值 开始------
	/**
	 * 解析 X/Y 当前输入对应的单次平移向量。
	 *
	 * @returns 单次平移向量。
	 */
	private resolveXYStepTranslationVector(): THREE.Vector3 {
		const deltaX = this.targetX ?? 0;
		const deltaY = this.targetY ?? 0;
		return this.createXYTranslationVector(deltaX, deltaY);
	}

	/**
	 * 解析 X/Y Repeat 使用的固定步长位移向量。
	 *
	 * @returns 固定步长位移向量。
	 */
	private resolveXYRepeatStepTranslationVector(): THREE.Vector3 {
		if (this.xyRepeatAxis === SketchCopyEntitiesXYRepeatAxis.X) {
			return this.createXYTranslationVector(this.xyRepeatStepX, 0);
		}
		if (this.xyRepeatAxis === SketchCopyEntitiesXYRepeatAxis.Y) {
			return this.createXYTranslationVector(0, this.xyRepeatStepY);
		}
		return new THREE.Vector3();
	}

	/**
	 * 根据 X/Y 数值创建草图局部坐标系中的世界平移向量。
	 *
	 * @param deltaX X 方向平移距离。
	 * @param deltaY Y 方向平移距离。
	 * @returns 世界平移向量。
	 */
	private createXYTranslationVector(deltaX: number, deltaY: number): THREE.Vector3 {
		const activeSketchGroup = this.resolveActiveSketchGroup();

		return activeSketchGroup
			? activeSketchGroup.referencePlane.xAxis.clone()
				.multiplyScalar(deltaX)
				.add(activeSketchGroup.referencePlane.yAxis.clone().multiplyScalar(deltaY))
			: new THREE.Vector3(deltaX, deltaY, 0);
	}
	//------ 复制实体XY重复数值 结束------

	/**
	 * 同步起点黄色标记。
	 *
	 * 说明：
	 * 1. Copy 命令在 From/To 模式下只需要一个起点抓手；
	 * 2. 起点标记必须固定在原始选择点，副本随鼠标预览移动；
	 * 3. 标记仅属于预览表达，不进入草图定义和模型历史。
	 */
	private syncFromPointMarker(): void {
		if (this.inputMode !== SketchEntitiesTransformInputMode.FromTo || !this.fromPoint) {
			this.clearFromPointMarker();
			return;
		}

		const markerAlreadyExists = !!this.fromPointMarkerRoot;
		const markerRoot = this.ensureFromPointMarkerRoot();
		markerRoot.position.copy(this.fromPoint.position);
		if (markerAlreadyExists) {
			this.viewer.requestRender();
			return;
		}
		// 黄色起点根只在首次创建时登记，后续固定标记不进入 mousemove 热路径。
		this.syncPreviewVisualLayering();
	}

	/**
	 * 初始化鼠标驱动预览态。
	 *
	 * @param basePoint 已选起点世界坐标。
	 */
	private initializePointerDrivenPreview(basePoint: THREE.Vector3): void {
		const activeSketchGroup = this.resolveActiveSketchGroup();
		if (!activeSketchGroup) {
			this.resetPointerDrivenPreview();
			return;
		}

		this.pointerDrivenPreviewPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(
			activeSketchGroup.referencePlane.normal.clone(),
			basePoint.clone(),
		);
		this.pointerDrivenPreviewPoint = basePoint.clone();
	}

	/**
	 * 重置鼠标驱动预览态。
	 */
	private resetPointerDrivenPreview(): void {
		this.pointerDrivenPreviewPlane = null;
		this.pointerDrivenPreviewPoint = null;
	}

	/**
	 * 解析鼠标驱动预览当前指向点。
	 *
	 * @param clientX 屏幕 X 坐标。
	 * @param clientY 屏幕 Y 坐标。
	 * @returns 捕捉后或自由平面上的目标点；无法求交时返回 null。
	 */
	private resolvePointerDrivenPreviewPoint(clientX: number, clientY: number): THREE.Vector3 | null {
		if (!this.pointerDrivenPreviewPlane) {
			return null;
		}

		const freePoint = this.planePointerIntersectionResolver.resolve(
			this.viewer,
			this.pointerDrivenPreviewPlane,
			clientX,
			clientY,
		);
		if (!freePoint) {
			return null;
		}

		this.pointSnapExclusionIds ??= createSketchEntitiesTransformPointSnapExclusionIds({
			entities: this.selectedEntities,
			extraEntities: this.copyPreviewSession?.getEntities(),
			geometryService: this.geometryService,
		});
		const snapResolution = resolveSketchEntitiesTransformPointSnap({
			viewer: this.viewer,
			clientX,
			clientY,
			worldPoint: freePoint,
			excludedTargetObjectIds: this.pointSnapExclusionIds,
		});
		return snapResolution?.position.clone() ?? freePoint;
	}

	/**
	 * 确保起点黄色标记根对象存在。
	 *
	 * @returns 起点标记根对象。
	 */
	private ensureFromPointMarkerRoot(): THREE.Group {
		if (this.fromPointMarkerRoot) {
			return this.fromPointMarkerRoot;
		}

		const markerSprite = new THREE.Sprite(PointsMaterialFactory.getReferenceSourceHighlightMaterial());
		markerSprite.name = '复制实体-起点黄色标记';
		markerSprite.scale.set(0.009, 0.009, 0.009);

		const markerRoot = new THREE.Group();
		markerRoot.name = '复制实体-起点黄色标记根对象';
		markerRoot.add(markerSprite);
		this.viewer.addObject(markerRoot);
		this.fromPointMarkerRoot = markerRoot;
		return markerRoot;
	}

	/**
	 * 清理起点黄色标记。
	 */
	private clearFromPointMarker(): void {
		if (!this.fromPointMarkerRoot) {
			return;
		}
		if (this.fromPointMarkerRoot.parent) {
			this.fromPointMarkerRoot.parent.remove(this.fromPointMarkerRoot);
		}
		this.fromPointMarkerRoot = null;
		this.syncPreviewVisualLayering();
	}

	/**
	 * 按当前复制副本域和黄色起点标记同步预览视觉层级。
	 *
	 * 该入口只允许在预览根集合发生变化时调用，连续鼠标移动不得重复遍历全部草图、
	 * 刷新尺寸布局或改写材质深度状态。
	 */
	private syncPreviewVisualLayering(): void {
		const previewRoots = [
			...(this.copyPreviewSession?.getEntities() ?? []),
			...(this.fromPointMarkerRoot ? [this.fromPointMarkerRoot] : []),
		];
		this.viewer.syncSketchEditingVisualLayering({
			previewRoots,
		});
	}
}
